import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {isMobile} from '@wandb/weave/components/WeavePanelBank/panelbankFlow';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, Label, Popup} from 'semantic-ui-react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import * as CustomRunColorsActions from '../../state/views/customRunColors/actions';
import * as CustomRunColorsTypes from '../../state/views/customRunColors/types';
import * as GroupSelectionsActions from '../../state/views/groupSelections/actions';
import * as GroupSelectionsTypes from '../../state/views/groupSelections/types';
import * as ViewHooks from '../../state/views/hooks';
import * as TempSelectionsActions from '../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../state/views/tempSelections/types';
import {BenchmarkProject} from '../../types/graphql';
import {runColor} from '../../util/colors';
import {safeLocalStorage} from '../../util/localStorage';
import {runPath, WBTableRowWithRunSetInfo} from '../../util/runhelpers';
import * as Run from '../../util/runs';
import * as SM from '../../util/selectionmanager';
import {runGroup as runGroupURL} from '../../util/urls';
import {versionGte} from '../../util/version';
import AlertsManagementModal from '../AlertsManagementModal';
import ColorPicker from '../ColorPicker';
import DeleteRunModal from '../DeleteRunModal';
import {EditableLabel} from '../elements/EditableLabel';
import {JobInfo} from '../Launch/utilTypes';
import {MultiStateCheckbox} from '../MultiStateCheckbox';
import StopRunModal from '../StopRunModal';
import SubmitToBenchmarkModal from '../SubmitToBenchmarkModal';
import {InstrumentedLoader} from '../utility/InstrumentedLoader';
import LaunchJobIcon from './LaunchJobIcon';
import {RunPendingResultsPopup} from './RunPendingResultsPopup';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {DisabledFeaturesMap} from './RunSelectorInner';
import {RunSelectorInteractTracker} from './RunSelectorInteractWrapper';

const StyledLoader = styled(InstrumentedLoader)`
  margin-left: -4px;
`;
const colorPickerStyle = {marginRight: '6px', marginBottom: '8px'};

export interface RunSelectorRowHeaderProps {
  benchmarkMeta?: BenchmarkProject;
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  disabledFeatures?: DisabledFeaturesMap;
  disableRunLink?: boolean;
  displayBenchmark?: boolean;
  displayTempCheckbox: boolean;
  entityName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  isFirstActiveRun?: boolean;
  isGroup?: boolean; // false if row is individual run, true if it's a group of runs
  isGroupExpanded: boolean;
  isInReport?: boolean;
  isLaunchActive?: boolean;
  isSingleMode?: boolean;
  isTableCollapsed?: boolean;
  loadingChildren?: boolean;
  onSelectRunName?: (runId: string) => void;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  selectedRunName?: string;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  tempSelectionsRef: TempSelectionsTypes.Ref;

  // If true, users won't be able to edit filter, grouping, etc.
  workspaceReadOnly?: boolean;
  // If true, users won't be able to rename runs, etc.
  projectReadOnly?: boolean;
  onGroupButtonClick?(): void;
  onTempCheckboxClick(): void;
  onVisibilityCheckboxClick(): void;
  saveRunName(run: Run.Run, newName: string): void;
  setLaunchDrawerRow(run: string, jobInfo: JobInfo): void;
  stopRun(id: string): Promise<any>; // from withMutations
}

type AllRunSelectorRowHeaderProps = RunSelectorRowHeaderProps &
  ReturnType<typeof useRunSelectorRowHeaderProps>;

const RunSelectorRowHeaderInnerComp: React.FC<AllRunSelectorRowHeaderProps> = ({
  benchmarkMeta,
  className,
  closeDeleteModal,
  closeStopModal,
  customRunColor,
  deleteModalOpen,
  disabledFeatures,
  disableRunLink,
  displayBenchmark,
  displayTempCheckbox,
  editingName,
  entityName,
  groupedBy,
  isAnyCellSelected,
  isFirstActiveRun,
  isGroup,
  isGroupExpanded,
  isInReport,
  isRunGroup,
  isSingleMode,
  isTableCollapsed,
  loadingChildren,
  projectReadOnly,
  onGroupButtonClick,
  onSelectRunName,
  onTempCheckboxClick,
  onVisibilityCheckboxClick,
  openDeleteModal,
  setLaunchDrawerRow,
  isLaunchActive,
  openStopModal,
  projectName,
  workspaceReadOnly,
  recursionDepth,
  row,
  runIsInBounds,
  saveRunName,
  setCustomRunColor,
  showArtifactCounts,
  showLogCounts,
  startEditingName,
  stopEditingName,
  stopModalOpen,
  stopRun,
  tempCheckboxState,
  tempSelectToggle,
  toggleSelection,
  uniqueId,
  visCheckboxState,
}) => {
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
  const [alertsModalViewed, setAlertsModalViewed] = useState(true);

  useLayoutEffect(() => {
    setAlertsModalViewed(
      safeLocalStorage.getItem('alertsModalViewed') === 'true'
    );
  }, []);

  const openBenchmarkModal = useCallback(() => {
    setShowBenchmarkModal(true);
  }, []);

  const closeBenchmarkModal = useCallback(() => {
    setShowBenchmarkModal(false);
  }, []);

  const runGroupText = groupedBy ? Run.getValue(row, groupedBy) : '';
  const groupedText = _.isEqual(groupedBy, Run.GROUP_BY_ALL_KEY)
    ? Run.keyDisplayName(groupedBy)
    : `${groupedBy ? Run.keyDisplayName(groupedBy) + ': ' : ''}${
        groupedBy ? runGroupText : ''
      }`;

  const stopRunAvailable = useCallback(() => {
    // TODO(adrnswanberg): Centralize this with similar logic in RunPageActions.tsx,
    // probably around when we start using the same `Run` type everywhere.
    const cliVersion =
      row._wandb['value.cli_version'] ?? row._wandb.cli_version;
    if (cliVersion == null || typeof cliVersion !== 'string') {
      return false;
    }

    const isJupyterRun =
      row._wandb['value.is_jupyter_run'] ?? row._wandb.is_jupyter_run ?? false;

    return versionGte(cliVersion, '0.8.6') && !isJupyterRun;
  }, [row]);

  const groupCounts = row.groupCounts ?? [];

  let runCount;
  let groupCount;
  if (groupCounts.length >= 2) {
    groupCount = groupCounts[groupCounts.length - 2];
    runCount = groupCounts[groupCounts.length - 1];
  } else if (groupCounts.length === 1) {
    [runCount] = groupCounts;
  }

  // Show color picker whenever we're not grouped, or when we're at
  // the deepest grouping level.
  const shouldShowColorPicker = isGroup
    ? groupCount == null
    : recursionDepth === 0;

  const showRowHeaderDropdown =
    !workspaceReadOnly && !projectReadOnly && !isGroup;

  return (
    <React.Fragment>
      {deleteModalOpen && (
        <DeleteRunModal
          entityName={entityName}
          isRunActive={row.state === 'running'}
          closeModal={closeDeleteModal}
          projectName={projectName}
          runDisplayName={row.displayName}
          runId={row.id}
          runName={row.name}
        />
      )}
      <StopRunModal
        shouldShowModal={stopModalOpen}
        onModalCanceled={closeStopModal}
        onStopRun={stopRun}
      />
      <RunPendingResultsPopup
        isPendingUpdates={row.pendingUpdates ?? false}
        trigger={
          <div
            className={`
          wb-tree-cell--header-row
          wb-tree-cell--name
          ${className ? className : ''}
          ${loadingChildren ? 'wb-tree-cell--loading-children' : ''}
          `}
            // onClick={isRunGroup ? undefined : onClick}
            style={{paddingLeft: 16 + recursionDepth * 26}}>
            {showRowHeaderDropdown && (
              <RowHeaderPopupDropdown
                displayBenchmark={
                  !!displayBenchmark &&
                  row.state !== 'running' &&
                  !row.benchmarkRun
                }
                displayStop={
                  row.state === 'running' &&
                  (row.sweep != null || stopRunAvailable())
                }
                editName={startEditingName}
                openStopModal={openStopModal}
                openDeleteModal={openDeleteModal}
                openBenchmarkModal={openBenchmarkModal}
              />
            )}
            {!workspaceReadOnly &&
              !isTableCollapsed &&
              (displayTempCheckbox || isAnyCellSelected ? (
                <MultiStateCheckbox
                  data-test="select-run-checkbox"
                  className="row-index-or-checkbox"
                  checked={tempCheckboxState}
                  onClick={() => {
                    tempSelectToggle();
                    onTempCheckboxClick();
                    const actionLocation = isInReport ? 'report' : 'runs table';
                    window.analytics?.track('Run selected', {
                      location: actionLocation,
                    });
                  }}
                />
              ) : (
                <div className="row-index-or-checkbox row-index">•</div>
              ))}

            {!isSingleMode && !disabledFeatures?.['toggle-visibility'] && (
              <RowHeaderVisCheckbox
                isInReport={isInReport}
                onVisibilityCheckboxClick={onVisibilityCheckboxClick}
                recursionDepth={recursionDepth}
                row={row}
                runIsInBounds={runIsInBounds}
                toggleSelection={toggleSelection}
                visCheckboxState={visCheckboxState}
              />
            )}
            {shouldShowColorPicker && (
              <ColorPicker
                activeColor={customRunColor}
                style={colorPickerStyle}
                setRunColor={setCustomRunColor}
                onClick={() => {
                  const actionLocation = isInReport ? 'report' : 'runs table';
                  window.analytics?.track('Run color dropdown clicked', {
                    location: actionLocation,
                  });
                }}
              />
            )}
            {isGroup && (
              <React.Fragment>
                <StyledLoader
                  active
                  inline
                  name="is-group-loader"
                  size="mini"
                />
                <Icon
                  style={{marginLeft: -6}}
                  className={`triangle ${isGroupExpanded ? 'down' : 'right'}`}
                  onClick={onGroupButtonClick}
                />
              </React.Fragment>
            )}
            <RunSelectorInteractTracker
              className={classNames({
                'run-row-name': true,
                failed: row.state === 'failed' && showLogCounts && !isGroup,
                'single-mode': isSingleMode && !isGroup,
              })}
              runSelector={uniqueId}>
              {isRunGroup ? (
                <Link
                  to={runGroupURL({
                    entityName,
                    projectName,
                    name: row.name,
                    tab: 'workspace',
                  })}
                  title={groupedText}>
                  {groupedText}
                </Link>
              ) : isGroup ? (
                <span title={groupedText}>{groupedText}</span>
              ) : editingName ? (
                <EditableLabel
                  key={row.name}
                  title={row.displayName}
                  onBlur={(newName: string) => {
                    saveRunName(row, newName);
                    stopEditingName();
                  }}
                  editing={editingName}
                  serverText={row.displayName}
                  extraMarginRight={
                    row.state === 'running' &&
                    isFirstActiveRun &&
                    !alertsModalViewed
                      ? '28px'
                      : '12px'
                  }
                  onClick={() =>
                    isSingleMode ? onSelectRunName?.(row.name) : false
                  } // we use the menu option to trigger editing mode, rather than direct click
                />
              ) : (
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <Tooltip
                    content={
                      <span
                        style={{
                          display: 'inline-block',
                          maxWidth: 300,
                          textAlign: 'left',
                          overflowWrap: 'anywhere',
                          alignSelf: 'flex-end',
                        }}>
                        {row.displayName}
                      </span>
                    }
                    position="top right"
                    trigger={
                      disableRunLink || isSingleMode ? (
                        <div
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {row.displayName}
                        </div>
                      ) : (
                        <Link
                          style={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          to={runPath({
                            entityName,
                            projectName,
                            runName: row.name,
                          })}>
                          {row.displayName}
                        </Link>
                      )
                    }
                  />
                  {isLaunchActive && row.state !== 'running' && !isMobile() && (
                    <LaunchJobIcon
                      projectName={projectName}
                      entityName={entityName}
                      row={row}
                      setLaunchDrawerRow={setLaunchDrawerRow}
                    />
                  )}
                </div>
              )}
            </RunSelectorInteractTracker>
            {isGroup && (
              <React.Fragment>
                {!_.isUndefined(groupCount) && (
                  <Label className="count-label">{groupCount}</Label>
                )}
                {!_.isUndefined(runCount) && (
                  <Label className="count-label">{runCount}</Label>
                )}
              </React.Fragment>
            )}
            {!isGroup && showArtifactCounts && (
              <>
                {row.inputArtifactsCount !== 0 && (
                  <Label className="count-label">
                    In {row.inputArtifactsCount}
                  </Label>
                )}
                {row.outputArtifactsCount !== 0 && (
                  <Label className="count-label">
                    Out {row.outputArtifactsCount}
                  </Label>
                )}
              </>
            )}

            {!isGroup && showLogCounts && row.logLineCount != null && (
              <Label className="count-label">{row.logLineCount}</Label>
            )}

            {row.state === 'running' && (
              <AlertsManagementModal
                location="runs table"
                color={globals.successText}
                onClose={() => setAlertsModalViewed(true)}
                displayIcon={
                  isFirstActiveRun && !alertsModalViewed
                    ? 'bell'
                    : 'active run indicator'
                }
              />
            )}
            {row.benchmarkRun && benchmarkMeta && (
              <Popup
                hoverable={true}
                trigger={<Icon className="wbic-ic-benchmark" />}
                horizontalOffset={10}
                flowing={true}
                size="tiny"
                content={
                  <span>
                    Submitted to{' '}
                    <Link
                      to={
                        '/' +
                        benchmarkMeta.entityName +
                        '/' +
                        benchmarkMeta.name +
                        '/benchmark'
                      }>
                      {benchmarkMeta.name}
                    </Link>{' '}
                    benchmark
                  </span>
                }
              />
            )}
          </div>
        }
      />
      {benchmarkMeta && (
        <SubmitToBenchmarkModal
          runId={row.id}
          run={row}
          runName={row.displayName}
          runSummary={row.summary}
          shouldShowModal={showBenchmarkModal}
          closeModal={closeBenchmarkModal}
          benchmarkMeta={benchmarkMeta}
          project={{name: projectName, entityName}}
        />
      )}
    </React.Fragment>
  );
};

const RunSelectorRowHeaderInner = memo(
  RunSelectorRowHeaderInnerComp,
  (prevProps, nextProps) => {
    if (prevProps.row.state !== nextProps.row.state) {
      return false;
    }
    if (prevProps.row.displayName !== nextProps.row.displayName) {
      return false;
    }
    const propsAreEqual = makePropsAreEqual({
      name: 'RunSelectorRowHeader',
      deep: ['groupKey', 'tempCheckboxState', 'customRunColors'],
      // Can ignore, the row remounts when run id changes. This will screw up polling though
      // We should pass the value in rather than row probably, or maybe just check if our value
      // changes here in shouldUpdate
      ignore: ['row', 'cellHoverProps'],
      ignoreFunctions: true,
      debug: false,
      verbose: true,
    });
    return propsAreEqual(prevProps, nextProps);
  }
);

function useRunSelectorRowHeaderProps(props: RunSelectorRowHeaderProps) {
  const grouping = ViewHooks.useWholeMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.grouping
  );
  const expandedRowAddresses = ViewHooks.useWholeMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.expandedRowAddresses
  );
  const visCheckboxState = ViewHooks.useWholeMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.getCheckedState(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  // For some reason TS infers the type as `boolean` without the
  // type cast even though `runIsInBounds()` returns `boolean | null`
  const runIsInBounds = ViewHooks.useWholeMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.runIsInBounds(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  ) as boolean | null;

  // TODO(adrnswanberg): This might not be the most efficient thing to do..
  const isAnyCellSelected = ViewHooks.useWholeMapped(
    props.tempSelectionsRef,
    state =>
      !(state.bounds.length === 0 && state.tree.length === 0) ||
      state.root === SM.Root.All
  );
  const tempCheckboxState = ViewHooks.useWholeMapped(
    props.tempSelectionsRef,
    state => {
      const tempGroupSelections = {
        grouping,
        selections: state,
        expandedRowAddresses,
      };
      return SM.getCheckedState(
        tempGroupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  const toggleSelection = ViewHooks.useViewAction(
    props.groupSelectionsRef,
    GroupSelectionsActions.toggleSelection
  );

  const tempSelectToggle = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectToggle,
    props.tempSelectionsRef,
    props.groupSelectionsRef,
    props.row,
    props.recursionDepth + 1
  );

  const uniqueId = Run.uniqueId(props.row, grouping);

  let customRunColor = ViewHooks.useWholeMapped(
    props.customRunColorsRef,
    runColors => runColors[uniqueId]
  );

  customRunColor = runColor(props.row, grouping, customRunColor);

  const setCustomRunColor = ViewHooks.useViewAction(
    props.customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );
  const setCustomRunColorCurried = useCallback(
    (color: string) => {
      setCustomRunColor(uniqueId, color);
    },
    [uniqueId, setCustomRunColor]
  );

  const groupedBy = grouping[props.recursionDepth];
  const isRunGroup = _.isEqual(groupedBy, {section: 'run', name: 'group'});

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const openStopModal = useCallback(
    () => setStopModalOpen(true),
    [setStopModalOpen]
  );
  const closeStopModal = useCallback(
    () => setStopModalOpen(false),
    [setStopModalOpen]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(
    () => setDeleteModalOpen(true),
    [setDeleteModalOpen]
  );
  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen(false),
    [setDeleteModalOpen]
  );

  const {id} = props.row;
  const {stopRun} = props;
  const stopRunCurried = useCallback(() => stopRun(id), [stopRun, id]);

  const [editingName, setEditingName] = useState(false);
  const startEditingName = useCallback(
    () => setEditingName(true),
    [setEditingName]
  );
  const stopEditingName = useCallback(
    () => setEditingName(false),
    [setEditingName]
  );

  return {
    closeDeleteModal,
    closeStopModal,
    customRunColor,
    deleteModalOpen,
    editingName,
    groupedBy,
    isAnyCellSelected,
    isRunGroup,
    openDeleteModal,
    openStopModal,
    runIsInBounds,
    setCustomRunColor: setCustomRunColorCurried,
    setLaunchDrawerRow: props.setLaunchDrawerRow,
    startEditingName,
    stopEditingName,
    stopModalOpen,
    stopRun: stopRunCurried,
    tempCheckboxState,
    tempSelectToggle,
    toggleSelection,
    uniqueId,
    visCheckboxState,
  };
}

export const RunSelectorRowHeader = (props: RunSelectorRowHeaderProps) => {
  const reduxProps = useRunSelectorRowHeaderProps(props);
  return <RunSelectorRowHeaderInner {...props} {...reduxProps} />;
};

type RowHeaderPopupDropdownProps = {
  displayBenchmark: boolean;
  displayStop: boolean;
  editName(): void;
  openDeleteModal(): void;
  openBenchmarkModal(): void;
  openStopModal(): void;
};

const RowHeaderPopupDropdown: React.FC<RowHeaderPopupDropdownProps> = ({
  displayBenchmark,
  displayStop,
  editName,
  openDeleteModal,
  openBenchmarkModal,
  openStopModal,
}) => {
  const options = useMemo(() => {
    const opts: Array<{
      key: string;
      text: string;
      icon: string | JSX.Element;
      onClick: () => any;
    }> = [];
    opts.push({
      key: 'edit',
      text: 'Rename run',
      icon: 'wbic-ic-edit',
      onClick: editName,
    });
    opts.push({
      key: 'delete',
      text: 'Delete run',
      icon: 'wbic-ic-delete',
      onClick: openDeleteModal,
    });
    if (displayBenchmark) {
      opts.push({
        key: 'benchmark',
        text: 'Submit to benchmark',
        icon: 'wbic-ic-benchmark',
        onClick: openBenchmarkModal,
      });
    }
    if (displayStop) {
      opts.push({
        key: 'stop',
        text: 'Stop run',
        icon: <LegacyWBIcon name="stop" />,
        onClick: openStopModal,
      });
    }
    return opts;
  }, [
    displayBenchmark,
    displayStop,
    editName,
    openBenchmarkModal,
    openDeleteModal,
    openStopModal,
  ]);

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const trigger = useMemo(
    () => (
      <LegacyWBIcon
        className="row-actions-button"
        name="overflow"
        title="menu"
        size="large"
        onClick={stopPropagation}
      />
    ),
    [stopPropagation]
  );

  return <PopupDropdown trigger={trigger} options={options} />;
};

type RowHeaderVisCheckboxProps = Pick<
  AllRunSelectorRowHeaderProps,
  | 'row'
  | 'recursionDepth'
  | 'runIsInBounds'
  | 'visCheckboxState'
  | 'toggleSelection'
  | 'onVisibilityCheckboxClick'
  | 'isInReport'
>;

const RowHeaderVisCheckbox: React.FC<RowHeaderVisCheckboxProps> = ({
  row,
  recursionDepth,
  runIsInBounds,
  visCheckboxState,
  toggleSelection,
  onVisibilityCheckboxClick,
  isInReport,
}) => {
  const checkbox = (
    <MultiStateCheckbox
      data-test="run-visibility-checkbox"
      checked={visCheckboxState}
      onClick={() => {
        toggleSelection(row, recursionDepth + 1);
        onVisibilityCheckboxClick();
        window.analytics?.track('Run visibility toggled', {
          location: isInReport ? 'report' : 'runs table',
        });
      }}
      theme="visibility"
    />
  );

  if (runIsInBounds == null) {
    return checkbox;
  }
  if (runIsInBounds) {
    return checkbox;
  }
  return (
    <Popup
      inverted
      position="right center"
      content="This run is hidden by a parallel coordinates or scatter plot. Deselect the range on that chart first."
      size="mini"
      on="hover"
      trigger={checkbox}
      offset={-3}
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the
          // table header
          boundariesElement: 'viewport',
        },
      }}
    />
  );
};
